import { action, atom } from '@reatom/framework'

import { AsyncStateN, pipe, Result } from '@blade/std-lib'
import { CustomEmojiId } from '@root/entities/premium'
import { dependenciesAtom } from '@root/features/shared/dependencies'
import { DomUtil, UnknownError } from '@root/shared'
import { apiRequest } from '@root/shared/api'
import { reatomRequest } from '@root/shared/lib'

import type { ActionPayload } from '@reatom/framework'
import type { PremiumId, PremiumMetaModel } from '@root/entities/premium'

export const previewEmojiRequestAction = action(
  async (
    ctx,
    { emojiBase64, premiumId }: { emojiBase64: string; premiumId: PremiumId },
  ) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)

    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.services.servicesPreviewEmojiCreate, [
        {
          emoji: emojiBase64,
          premiumId,
        },
      ]),
    )

    return result
  },
  'previewEmojiRequestAction',
)
type PreviewResult = Awaited<ActionPayload<typeof previewEmojiRequestAction>>

export type PreviewEmojiRequestError = Result.Result.Failure<PreviewResult>

type EmojiPreview = {
  customEmojiId: CustomEmojiId
}

export const reatomPreviewEmoji = ({
  premiumMeta,
}: {
  premiumMeta: PremiumMetaModel
}) => {
  const execInternalAction = action(async (ctx, { emoji }: { emoji: File }) => {
    const base64Result = await ctx.schedule(() => DomUtil.blobToBase64(emoji))
    if (Result.isFailure(base64Result)) {
      return Result.fail(base64Result.failure)
    }
    const premiumId = ctx.get(premiumMeta.premiumId)
    if (!premiumId) {
      return Result.fail(UnknownError.fromUnknown('missing premium id'))
    }

    return previewEmojiRequestAction(ctx, {
      emojiBase64: base64Result.success,
      premiumId,
    })
  }, 'execInternalAction')

  const { executeAction, requestStateAtom, resetStateAction } = reatomRequest({
    execAction: execInternalAction,
    name: `reatomPreviewEmoji`,
  })

  const previewRequestStateAtom = atom((ctx) => {
    const requestState = ctx.spy(requestStateAtom)

    return pipe(
      requestState,
      AsyncStateN.map((it): EmojiPreview => {
        return {
          customEmojiId: CustomEmojiId(it.customEmojiId),
        }
      }),
    )
  }, 'previewRequestStateAtom')

  return {
    previewRequestStateAtom,
    resetPreviewStateAction: resetStateAction,
    loadPreviewLink: executeAction,
  }
}
